import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Send, 
  Users, 
  ArrowLeft,
  CheckCircle,
  MessageSquare,
  Lightbulb,
  Sparkles,
  Target,
  Timer,
  Brain,
  Wand2,
  Trophy
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { databaseService } from '@/services/database'
import { realtimeService } from '@/services/realtime'
import { Room, Player, UserWord } from '@/types'
import { useTranslation } from 'react-i18next'
import RoomClosedModal from '@/components/RoomClosedModal'

export default function UserWordPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [room, setRoom] = useState<Room | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null)
  const [userWord, setUserWord] = useState('')
  const [submittedWord, setSubmittedWord] = useState<UserWord | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [allUserWords, setAllUserWords] = useState<UserWord[]>([])
  const [wordSubmissionTimeLimit] = useState(60) // 60 seconds default
  const [showRoomClosed, setShowRoomClosed] = useState(false)

  const amIHost = () => {
    try {
      if (currentPlayer && typeof currentPlayer.isHost === 'boolean') return !!currentPlayer.isHost
      const saved = localStorage.getItem('currentPlayer')
      if (!saved) return false
      const me = JSON.parse(saved)
      return !!me?.isHost
    } catch {
      return !!currentPlayer?.isHost
    }
  }

  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    // Get current player from localStorage
    const savedPlayer = localStorage.getItem('currentPlayer')
    if (savedPlayer) {
      setCurrentPlayer(JSON.parse(savedPlayer))
    }

    loadRoom()
    const cleanupRealtime = setupRealtimeSubscription()
    setupRealtimeBroadcasts()
    
    // Cleanup function
    return () => {
      if (cleanupRealtime) {
        cleanupRealtime()
      }
      // Note: Don't cleanup room here as it causes issues with rapid page transitions
      // Cleanup will happen when user actually leaves the room via buttons
    }
  }, [roomId])

  // Presence kanalı: odaya katıl (host dahil)
  useEffect(() => {
    if (!roomId || !currentPlayer) return
    const isHost = !!room?.players.find(p => p.id === currentPlayer.id)?.isHost || !!currentPlayer.isHost
    const isReady = !!room?.players.find(p => p.id === currentPlayer.id)?.isReady || !!currentPlayer.isReady
    realtimeService.joinRoom(roomId, {
      id: currentPlayer.id,
      name: currentPlayer.name,
      isHost,
      isReady
    })
  }, [roomId, currentPlayer?.id, currentPlayer?.name, room?.players])

  // Host presence kontrolü: host ayrılırsa modalı aç
  useEffect(() => {
    if (!roomId) return
    const playerJson = localStorage.getItem('currentPlayer')
    const me: Player | null = playerJson ? JSON.parse(playerJson) : null
    if (!me) return

    realtimeService.onPresenceSync(roomId, (users) => {
      const presentIds = new Set(users.map(u => u.id))
      const hostPlayerId = room?.players.find(p => p.isHost)?.id
      if (hostPlayerId && !presentIds.has(hostPlayerId) && !me.isHost) {
        setShowRoomClosed(true)
      }
    })
  }, [roomId, room?.players])

  // Timer effect for word submission
  useEffect(() => {
    if (!room || room.status !== 'waiting') return

    const updateTimeFromServer = async () => {
      try {
        // For word submission, we use countdown from wordSubmissionTimeLimit starting at roundStartedAt
        const startedAt = room.roundStartedAt ? new Date(room.roundStartedAt).getTime() : Date.now()
        const elapsed = Date.now() - startedAt
        const remaining = Math.max(0, wordSubmissionTimeLimit * 1000 - elapsed)
        setTimeLeft(Math.ceil(remaining / 1000))

        if (remaining <= 0 && !submittedWord) {
          // Time expired, auto-submit empty word or handle timeout
          console.log('Word submission time expired')
        }
      } catch (error) {
        console.error('Error calculating time:', error)
      }
    }

    // Update immediately
    updateTimeFromServer()

    // Then update every second
    const interval = setInterval(updateTimeFromServer, 1000)

    return () => clearInterval(interval)
  }, [room?.id, room?.status, room?.roundStartedAt, submittedWord, wordSubmissionTimeLimit])

  // Check if all players submitted words
  useEffect(() => {
    if (room && allUserWords.length === room.players.length && room.players.length > 0) {
      // All players submitted, start prompt writing phase
      console.log('All players submitted words, starting prompt writing phase')
      startPromptWritingPhase()
    }
  }, [allUserWords.length, room?.players.length])

  const loadRoom = async () => {
    try {
      const roomData = await databaseService.getRoom(roomId!)
      setRoom(roomData)
      if (roomData.status === 'finished') {
        if (!amIHost()) setShowRoomClosed(true)
        return
      }
      
      if (roomData.status === 'prompt_writing') {
        navigate(`/game/${roomId}`)
        return
      }
      
      if (roomData.status === 'voting') {
        navigate(`/voting/${roomId}`)
        return
      }
      
      if (roomData.status === 'results') {
        navigate(`/results/${roomId}`)
        return
      }

      // Load current round user words
      const words = await databaseService.getUserWords(roomId!, roomData.roundNumber)
      setAllUserWords(words)

      // Check if current player already submitted for current round
      if (currentPlayer) {
        const userWord = words.find(w => w.playerId === currentPlayer.id && w.roundNumber === roomData.roundNumber)
        setSubmittedWord(userWord || null)
        if (!userWord) {
          setUserWord('')
        }
      } else {
        setSubmittedWord(null)
        setUserWord('')
      }

      // Set round start time if not set
      if (roomData.status === 'waiting' && !roomData.roundStartedAt) {
        await databaseService.setRoundStartTime(roomId!)
      }
    } catch (error: any) {
      setError(error.message || t('userword.errorUnknown'))
    } finally {
      setLoading(false)
    }
  }

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`user-word-${roomId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'rooms',
        filter: `id=eq.${roomId}`
      }, (payload) => {
        console.log('Room changed:', payload)
        const dbRoom: any = (payload as any).new
        if (dbRoom && dbRoom.id === roomId) {
          if (dbRoom.status === 'finished') {
            if (!amIHost()) setShowRoomClosed(true)
            return
          }
          if (dbRoom.status === 'prompt_writing') {
            console.log('Room status changed to prompt_writing, navigating to game')
            navigate(`/game/${roomId}`)
            return
          }
          
          setRoom((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              status: dbRoom.status ?? prev.status,
              roundStartedAt: dbRoom.round_started_at ?? prev.roundStartedAt,
              roundNumber: dbRoom.round_number ?? prev.roundNumber,
            }
          })
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_words',
        filter: `room_id=eq.${roomId}`
      }, async (payload) => {
        console.log('User word changed:', payload)
        if (!roomId) return
        try {
          const words = await databaseService.getUserWords(roomId, room?.roundNumber || 1)
          setAllUserWords(words)
          
          // Update submittedWord if it's the current user's word
          if (currentPlayer) {
            const userWord = words.find(w => w.playerId === currentPlayer.id && w.roundNumber === (room?.roundNumber || 1))
            if (userWord) {
              setSubmittedWord(userWord)
            }
          }
        } catch (e) {
          console.error('Error syncing user words after change:', e)
        }
      })
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch (error) {
        console.log('WebSocket cleanup completed')
      }
    }
  }

  const setupRealtimeBroadcasts = () => {
    if (!roomId) return
    realtimeService.on(roomId, 'word_submitted', (payload) => {
      if (!payload) return
      console.log('Received word_submitted broadcast:', payload)
      loadRoom() // Reload to get updated word list
    })
  }

  const submitUserWord = async () => {
    if (!currentPlayer || !room || !userWord.trim()) {
      return
    }

    if (userWord.trim().length < 2) {
      setError(t('userword.errors.minLen'))
      return
    }

    if (userWord.trim().length > 100) {
      setError(t('userword.errors.maxLen'))
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const wordData = await databaseService.submitUserWord(
        room.id,
        currentPlayer.id,
        room.roundNumber,
        userWord.trim()
      )

      setSubmittedWord(wordData)
      
      // Notify others
      if (roomId) {
        await realtimeService.broadcast(roomId, 'word_submitted', {
          playerId: currentPlayer.id,
          wordId: wordData.id,
        })
      }

      // Reload to get updated word list
      await loadRoom()
    } catch (error: any) {
      setError(error.message || t('userword.errors.submit'))
    } finally {
      setSubmitting(false)
    }
  }

  const startPromptWritingPhase = async () => {
    try {
      if (!roomId) return
      
      const selectedWord = await databaseService.startPromptWritingWithUserWords(roomId, room?.roundNumber || 1)
      console.log('Selected word for prompt writing:', selectedWord)
      
      // Navigation will be handled by realtime subscription
    } catch (error: any) {
      setError(error.message || 'Prompt yazma aşaması başlatılırken hata oluştu')
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-white/20 border-t-emerald-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-20 h-20 border-2 border-emerald-400/30 rounded-full animate-pulse mx-auto"></div>
            <div className="absolute inset-4 w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-gradient-emerald">{t('userword.loading')}</h3>
            <p className="text-white/80">Kelimeler hazırlanıyor...</p>
            <div className="flex items-center justify-center space-x-2">
              <Brain className="w-5 h-5 text-emerald-400 animate-pulse" />
              <Sparkles className="w-4 h-4 text-teal-400 animate-bounce" />
              <Wand2 className="w-5 h-5 text-green-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="text-center space-y-4">
        <div className="card max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-red-600 mb-4">{t('userword.errorTitle')}</h2>
          <p className="text-gray-600 mb-4">{error || t('userword.errorUnknown')}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            {t('userword.backHome')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), title (center), timer (right) */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={() => navigate(`/lobby/${roomId}`)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Lobby</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex items-center justify-center space-x-2"
              >
                <Target className="w-4 h-4 text-emerald-400" />
                <h1 className="text-xl font-bold text-gradient-emerald">{t('userword.round', { num: room.roundNumber })}</h1>
                <Sparkles className="w-4 h-4 text-teal-400" />
              </motion.div>
            </div>
            <div className="flex-shrink-0">
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl backdrop-blur-sm border ${
                timeLeft < 30 
                  ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                  : 'bg-emerald-500/20 border-emerald-400/50 text-emerald-400'
              }`}>
                <Timer className="w-4 h-4" />
                <span className="font-mono text-base font-bold">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          </div>
          {/* Mobile subtitle */}
          <div className="mt-1 flex items-center justify-center space-x-2">
            <Brain className="w-3 h-3 text-emerald-400" />
            <p className="text-white/80 font-medium text-xs">{t('userword.phase')}</p>
            <Wand2 className="w-3 h-3 text-teal-400" />
          </div>
        </div>

        {/* Desktop: enhanced layout */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <motion.button
            onClick={() => navigate(`/lobby/${roomId}`)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Lobby</span>
          </motion.button>
          
          <div className="text-center flex-1">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="flex items-center justify-center space-x-2 mb-1"
            >
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gradient-emerald">{t('userword.round', { num: room.roundNumber })}</h1>
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-teal-400" />
            </motion.div>
            <div className="flex items-center justify-center space-x-2">
              <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
              <p className="text-white/80 font-medium text-sm sm:text-base">{t('userword.phase')}</p>
              <Wand2 className="w-3 h-3 sm:w-4 sm:h-4 text-teal-400" />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl backdrop-blur-sm border ${
              timeLeft < 30 
                ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                : 'bg-emerald-500/20 border-emerald-400/50 text-emerald-400'
            }`}>
              <Timer className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Instructions */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="card-gradient text-center relative overflow-hidden"
      >
        {/* Background glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-green-500/10 animate-pulse"></div>
        
        <div className="relative z-10">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center space-x-3 mb-4"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">{t('userword.instructionsTitle')}</h2>
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-green-600 rounded-2xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-3"
          >
            <p className="text-white/90 text-base leading-relaxed">{t('userword.instructionsBody1')} {t('userword.instructionsBody2')}</p>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-white/20">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-white/80 text-sm font-medium">Örnek Kelimeler</span>
              </div>
              <p className="text-white/70 text-sm">{t('userword.examples')}</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Enhanced Word Input */}
      {!submittedWord ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('userword.inputTitle')}</h2>
              <p className="text-white/70">Yaratıcılığını konuştur</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="relative">
              <input
                type="text"
                value={userWord}
                onChange={(e) => setUserWord(e.target.value)}
                placeholder={t('userword.placeholder')}
                className="w-full h-14 px-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-white placeholder-white/50 transition-all duration-300 hover:bg-white/10 focus:bg-white/10 text-lg"
                maxLength={100}
              />
              <div className="absolute bottom-3 right-3 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg">
                <span className="text-xs text-white/60">{userWord.length}/100</span>
              </div>
            </div>
            
            <div className="flex justify-center">
              <motion.button
                onClick={submitUserWord}
                disabled={submitting || !userWord.trim() || userWord.trim().length < 2}
                whileHover={{ scale: userWord.trim().length >= 2 ? 1.05 : 1 }}
                whileTap={{ scale: userWord.trim().length >= 2 ? 0.95 : 1 }}
                className="px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{t('userword.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('userword.submit')}</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-gradient bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400/50"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('userword.submittedTitle')}</h2>
              <p className="text-white/70">{t('userword.waitingOthers')}</p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
            <div className="flex items-start space-x-3">
              <Trophy className="w-5 h-5 text-emerald-400 mt-1" />
              <div>
                <span className="text-emerald-300 font-semibold text-sm">Senin Kelimen:</span>
                <p className="text-white font-medium text-lg leading-relaxed mt-1">{submittedWord.wordOrPhrase}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Enhanced Players Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-gradient"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{t('userword.playersTitle')}</h2>
            <p className="text-white/70 text-sm">{allUserWords.length}/{room.players.length} kelime gönderildi</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {room.players.map((player, index) => {
            const playerWord = allUserWords.find(w => w.playerId === player.id && w.roundNumber === room.roundNumber)
            const hasSubmitted = !!playerWord
            
            return (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="flex items-center justify-between p-4 rounded-2xl border backdrop-blur-sm bg-white/5 border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white text-sm font-bold shadow-lg ${
                    hasSubmitted 
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                      : 'bg-gradient-to-br from-gray-500 to-gray-600'
                  }`}>
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-white">{player.name}</span>
                      {player.id === currentPlayer?.id && (
                        <span className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {t('userword.you')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {hasSubmitted ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">{t('userword.submitted')}</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-white/60">{t('userword.typing')}</span>
                    </>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* Enhanced Progress Indicator */}
      {allUserWords.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`card-gradient text-center ${
            allUserWords.length === room.players.length 
              ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400/50'
              : 'bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-blue-400/50'
          }`}
        >
          <div className="space-y-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto ${
              allUserWords.length === room.players.length 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                : 'bg-gradient-to-br from-blue-500 to-indigo-600'
            }`}>
              {allUserWords.length === room.players.length ? (
                <Trophy className="w-8 h-8 text-white" />
              ) : (
                <Timer className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <p className="text-white font-semibold text-lg">{t('userword.submittedCount', { submitted: allUserWords.length, total: room.players.length })}</p>
              {allUserWords.length === room.players.length && (
                <motion.p 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-green-300 font-bold mt-2 text-lg"
                >
                  ✨ {t('userword.allReady')}
                </motion.p>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(allUserWords.length / room.players.length) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className={`h-full rounded-full ${
                  allUserWords.length === room.players.length 
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                    : 'bg-gradient-to-r from-blue-400 to-indigo-500'
                }`}
              />
            </div>
          </div>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <p className="text-red-600 text-center">{error}</p>
        </motion.div>
      )}

      {/* Room Closed Modal */}
      <RoomClosedModal
        isOpen={showRoomClosed}
        onClose={() => {
          try {
            if (roomId) {
              realtimeService.cleanupRoom(roomId)
              sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
            }
          } finally {
            navigate('/')
          }
        }}
      />
    </div>
  )
}
